/**
 * ItemData.ts
 * Frontend item definitions matching ItemData.sol contract
 * All 60 items with stats, restrictions, and effects
 */

import { ItemType, Element, CharacterClass, StatusEffect } from '../../lib/types/game';

export interface ItemDefinition {
  id: number;
  name: string;
  type: ItemType;
  power: number;
  accuracy: number;
  critBonus: number;
  element: Element;
  spellEffect: number; // StatusEffect for consumables
  price: number;
  classRestriction: number; // Bitmask of allowed classes
  description: string;
  stackable: boolean;
}

// Class restriction bitmasks
export const CLASS_RESTRICTIONS = {
  ALL_CLASSES: 0x3F,      // 111111 - all classes
  FIGHTERS_ONLY: 0x01,    // 000001 - Fighter only
  MAGES_ONLY: 0x38,       // 111000 - RedMage, WhiteMage, BlackMage
  PHYSICAL_CLASSES: 0x07, // 000111 - Fighter, Thief, Monk
  NO_MONK: 0x3B,          // 111011 - all except Monk
  THIEF_ONLY: 0x02,       // 000010 - Thief only
  MONK_ONLY: 0x04,        // 000100 - Monk only
  RED_MAGE_ONLY: 0x08,    // 001000 - RedMage only
  WHITE_MAGE_ONLY: 0x10,  // 010000 - WhiteMage only
  BLACK_MAGE_ONLY: 0x20,  // 100000 - BlackMage only
  FIGHTER_RED: 0x09,      // 001001 - Fighter, RedMage
  CONSUMABLE: 0x3F,       // 111111 - all can use
} as const;

/**
 * All item definitions (ID 1-60)
 */
export const ITEMS: Record<number, ItemDefinition> = {
  // =========================================================================
  // WEAPONS (ID 1-15)
  // =========================================================================

  1: {
    id: 1,
    name: 'Wooden Staff',
    type: ItemType.WEAPON,
    power: 6,
    accuracy: 10,
    critBonus: 0,
    element: Element.NONE,
    spellEffect: 0,
    price: 50,
    classRestriction: CLASS_RESTRICTIONS.MAGES_ONLY,
    description: 'Basic staff for mages',
    stackable: false,
  },

  2: {
    id: 2,
    name: 'Small Knife',
    type: ItemType.WEAPON,
    power: 5,
    accuracy: 15,
    critBonus: 5,
    element: Element.NONE,
    spellEffect: 0,
    price: 100,
    classRestriction: CLASS_RESTRICTIONS.ALL_CLASSES,
    description: 'Anyone can use this',
    stackable: false,
  },

  3: {
    id: 3,
    name: 'Rapier',
    type: ItemType.WEAPON,
    power: 9,
    accuracy: 20,
    critBonus: 8,
    element: Element.NONE,
    spellEffect: 0,
    price: 200,
    classRestriction: CLASS_RESTRICTIONS.NO_MONK,
    description: 'Quick thrusting sword',
    stackable: false,
  },

  4: {
    id: 4,
    name: 'Short Sword',
    type: ItemType.WEAPON,
    power: 15,
    accuracy: 20,
    critBonus: 5,
    element: Element.NONE,
    spellEffect: 0,
    price: 550,
    classRestriction: CLASS_RESTRICTIONS.PHYSICAL_CLASSES,
    description: 'Standard warrior blade',
    stackable: false,
  },

  5: {
    id: 5,
    name: 'Scimitar',
    type: ItemType.WEAPON,
    power: 18,
    accuracy: 22,
    critBonus: 10,
    element: Element.NONE,
    spellEffect: 0,
    price: 800,
    classRestriction: CLASS_RESTRICTIONS.PHYSICAL_CLASSES,
    description: 'Curved blade',
    stackable: false,
  },

  6: {
    id: 6,
    name: 'Long Sword',
    type: ItemType.WEAPON,
    power: 22,
    accuracy: 25,
    critBonus: 5,
    element: Element.NONE,
    spellEffect: 0,
    price: 1500,
    classRestriction: 0x03, // Fighter, Thief
    description: 'Heavy two-handed sword',
    stackable: false,
  },

  7: {
    id: 7,
    name: 'Silver Sword',
    type: ItemType.WEAPON,
    power: 30,
    accuracy: 30,
    critBonus: 8,
    element: Element.HOLY,
    spellEffect: 0,
    price: 4000,
    classRestriction: CLASS_RESTRICTIONS.FIGHTER_RED,
    description: 'Blessed silver blade',
    stackable: false,
  },

  8: {
    id: 8,
    name: 'Ice Brand',
    type: ItemType.WEAPON,
    power: 35,
    accuracy: 30,
    critBonus: 5,
    element: Element.ICE,
    spellEffect: 0,
    price: 7500,
    classRestriction: CLASS_RESTRICTIONS.FIGHTER_RED,
    description: 'Frozen magical blade',
    stackable: false,
  },

  9: {
    id: 9,
    name: 'Flame Sword',
    type: ItemType.WEAPON,
    power: 38,
    accuracy: 32,
    critBonus: 10,
    element: Element.FIRE,
    spellEffect: 0,
    price: 10000,
    classRestriction: CLASS_RESTRICTIONS.FIGHTERS_ONLY,
    description: 'Burning blade',
    stackable: false,
  },

  10: {
    id: 10,
    name: 'Dragon Sword',
    type: ItemType.WEAPON,
    power: 42,
    accuracy: 35,
    critBonus: 12,
    element: Element.NONE,
    spellEffect: 0,
    price: 15000,
    classRestriction: CLASS_RESTRICTIONS.FIGHTERS_ONLY,
    description: 'Forged from dragon scales',
    stackable: false,
  },

  11: {
    id: 11,
    name: 'Sun Blade',
    type: ItemType.WEAPON,
    power: 48,
    accuracy: 40,
    critBonus: 15,
    element: Element.HOLY,
    spellEffect: 0,
    price: 25000,
    classRestriction: CLASS_RESTRICTIONS.FIGHTERS_ONLY,
    description: 'Radiant holy sword',
    stackable: false,
  },

  12: {
    id: 12,
    name: 'Masamune',
    type: ItemType.WEAPON,
    power: 56,
    accuracy: 50,
    critBonus: 25,
    element: Element.NONE,
    spellEffect: 0,
    price: 0,
    classRestriction: CLASS_RESTRICTIONS.FIGHTERS_ONLY,
    description: 'Legendary katana',
    stackable: false,
  },

  13: {
    id: 13,
    name: 'Battle Axe',
    type: ItemType.WEAPON,
    power: 26,
    accuracy: 15,
    critBonus: 20,
    element: Element.NONE,
    spellEffect: 0,
    price: 2500,
    classRestriction: CLASS_RESTRICTIONS.FIGHTERS_ONLY,
    description: 'Heavy crushing weapon',
    stackable: false,
  },

  14: {
    id: 14,
    name: 'Iron Hammer',
    type: ItemType.WEAPON,
    power: 20,
    accuracy: 18,
    critBonus: 5,
    element: Element.NONE,
    spellEffect: 0,
    price: 1200,
    classRestriction: CLASS_RESTRICTIONS.PHYSICAL_CLASSES,
    description: 'Blunt force weapon',
    stackable: false,
  },

  15: {
    id: 15,
    name: 'Nunchaku',
    type: ItemType.WEAPON,
    power: 12,
    accuracy: 20,
    critBonus: 10,
    element: Element.NONE,
    spellEffect: 0,
    price: 400,
    classRestriction: CLASS_RESTRICTIONS.MONK_ONLY,
    description: 'Monk weapon',
    stackable: false,
  },

  // =========================================================================
  // ARMOR (ID 16-25)
  // =========================================================================

  16: {
    id: 16,
    name: 'Cloth',
    type: ItemType.ARMOR,
    power: 1,
    accuracy: 0,
    critBonus: 0,
    element: Element.NONE,
    spellEffect: 0,
    price: 10,
    classRestriction: CLASS_RESTRICTIONS.ALL_CLASSES,
    description: 'Basic cloth armor',
    stackable: false,
  },

  17: {
    id: 17,
    name: 'Leather Armor',
    type: ItemType.ARMOR,
    power: 4,
    accuracy: 0,
    critBonus: 0,
    element: Element.NONE,
    spellEffect: 0,
    price: 80,
    classRestriction: CLASS_RESTRICTIONS.NO_MONK,
    description: 'Light leather protection',
    stackable: false,
  },

  18: {
    id: 18,
    name: 'Chain Mail',
    type: ItemType.ARMOR,
    power: 8,
    accuracy: 0,
    critBonus: 0,
    element: Element.NONE,
    spellEffect: 0,
    price: 500,
    classRestriction: CLASS_RESTRICTIONS.PHYSICAL_CLASSES,
    description: 'Metal chain armor',
    stackable: false,
  },

  19: {
    id: 19,
    name: 'Iron Armor',
    type: ItemType.ARMOR,
    power: 12,
    accuracy: 0,
    critBonus: 0,
    element: Element.NONE,
    spellEffect: 0,
    price: 1500,
    classRestriction: CLASS_RESTRICTIONS.FIGHTERS_ONLY,
    description: 'Heavy plate armor',
    stackable: false,
  },

  20: {
    id: 20,
    name: 'Silver Armor',
    type: ItemType.ARMOR,
    power: 16,
    accuracy: 0,
    critBonus: 0,
    element: Element.HOLY,
    spellEffect: 0,
    price: 5000,
    classRestriction: CLASS_RESTRICTIONS.FIGHTERS_ONLY,
    description: 'Blessed silver plate',
    stackable: false,
  },

  21: {
    id: 21,
    name: 'Flame Armor',
    type: ItemType.ARMOR,
    power: 18,
    accuracy: 0,
    critBonus: 0,
    element: Element.FIRE,
    spellEffect: 0,
    price: 8000,
    classRestriction: CLASS_RESTRICTIONS.FIGHTERS_ONLY,
    description: 'Fire-resistant armor',
    stackable: false,
  },

  22: {
    id: 22,
    name: 'Ice Armor',
    type: ItemType.ARMOR,
    power: 18,
    accuracy: 0,
    critBonus: 0,
    element: Element.ICE,
    spellEffect: 0,
    price: 8000,
    classRestriction: CLASS_RESTRICTIONS.FIGHTERS_ONLY,
    description: 'Frost-resistant armor',
    stackable: false,
  },

  23: {
    id: 23,
    name: 'Dragon Armor',
    type: ItemType.ARMOR,
    power: 24,
    accuracy: 0,
    critBonus: 0,
    element: Element.NONE,
    spellEffect: 0,
    price: 20000,
    classRestriction: CLASS_RESTRICTIONS.FIGHTERS_ONLY,
    description: 'Dragon scale armor',
    stackable: false,
  },

  24: {
    id: 24,
    name: 'Black Robe',
    type: ItemType.ARMOR,
    power: 6,
    accuracy: 0,
    critBonus: 0,
    element: Element.DARK,
    spellEffect: 0,
    price: 2000,
    classRestriction: CLASS_RESTRICTIONS.BLACK_MAGE_ONLY,
    description: 'Black mage robes',
    stackable: false,
  },

  25: {
    id: 25,
    name: 'White Robe',
    type: ItemType.ARMOR,
    power: 6,
    accuracy: 0,
    critBonus: 0,
    element: Element.HOLY,
    spellEffect: 0,
    price: 2000,
    classRestriction: CLASS_RESTRICTIONS.WHITE_MAGE_ONLY,
    description: 'White mage robes',
    stackable: false,
  },

  // =========================================================================
  // HELMETS (ID 26-31)
  // =========================================================================

  26: {
    id: 26,
    name: 'Cap',
    type: ItemType.HELMET,
    power: 1,
    accuracy: 0,
    critBonus: 0,
    element: Element.NONE,
    spellEffect: 0,
    price: 50,
    classRestriction: CLASS_RESTRICTIONS.ALL_CLASSES,
    description: 'Simple cloth cap',
    stackable: false,
  },

  27: {
    id: 27,
    name: 'Wooden Helmet',
    type: ItemType.HELMET,
    power: 2,
    accuracy: 0,
    critBonus: 0,
    element: Element.NONE,
    spellEffect: 0,
    price: 150,
    classRestriction: CLASS_RESTRICTIONS.NO_MONK,
    description: 'Wooden headguard',
    stackable: false,
  },

  28: {
    id: 28,
    name: 'Iron Helmet',
    type: ItemType.HELMET,
    power: 4,
    accuracy: 0,
    critBonus: 0,
    element: Element.NONE,
    spellEffect: 0,
    price: 800,
    classRestriction: CLASS_RESTRICTIONS.PHYSICAL_CLASSES,
    description: 'Metal helmet',
    stackable: false,
  },

  29: {
    id: 29,
    name: 'Silver Helmet',
    type: ItemType.HELMET,
    power: 6,
    accuracy: 0,
    critBonus: 0,
    element: Element.HOLY,
    spellEffect: 0,
    price: 2500,
    classRestriction: CLASS_RESTRICTIONS.FIGHTERS_ONLY,
    description: 'Blessed helm',
    stackable: false,
  },

  30: {
    id: 30,
    name: 'Dragon Helmet',
    type: ItemType.HELMET,
    power: 8,
    accuracy: 0,
    critBonus: 0,
    element: Element.NONE,
    spellEffect: 0,
    price: 8000,
    classRestriction: CLASS_RESTRICTIONS.FIGHTERS_ONLY,
    description: 'Dragon bone helm',
    stackable: false,
  },

  31: {
    id: 31,
    name: 'Ribbon',
    type: ItemType.HELMET,
    power: 2,
    accuracy: 0,
    critBonus: 0,
    element: Element.NONE,
    spellEffect: 0,
    price: 0,
    classRestriction: CLASS_RESTRICTIONS.ALL_CLASSES,
    description: 'Prevents all status effects',
    stackable: false,
  },

  // =========================================================================
  // GLOVES (ID 32-35)
  // =========================================================================

  32: {
    id: 32,
    name: 'Leather Gloves',
    type: ItemType.GLOVES,
    power: 1,
    accuracy: 5,
    critBonus: 0,
    element: Element.NONE,
    spellEffect: 0,
    price: 100,
    classRestriction: CLASS_RESTRICTIONS.ALL_CLASSES,
    description: 'Basic hand protection',
    stackable: false,
  },

  33: {
    id: 33,
    name: 'Iron Gloves',
    type: ItemType.GLOVES,
    power: 2,
    accuracy: 5,
    critBonus: 0,
    element: Element.NONE,
    spellEffect: 0,
    price: 500,
    classRestriction: CLASS_RESTRICTIONS.PHYSICAL_CLASSES,
    description: 'Metal gauntlets',
    stackable: false,
  },

  34: {
    id: 34,
    name: 'Silver Gloves',
    type: ItemType.GLOVES,
    power: 4,
    accuracy: 8,
    critBonus: 5,
    element: Element.HOLY,
    spellEffect: 0,
    price: 2000,
    classRestriction: 0x0F,
    description: 'Holy silver gauntlets',
    stackable: false,
  },

  35: {
    id: 35,
    name: 'Power Gauntlet',
    type: ItemType.GLOVES,
    power: 6,
    accuracy: 10,
    critBonus: 10,
    element: Element.NONE,
    spellEffect: 0,
    price: 8000,
    classRestriction: CLASS_RESTRICTIONS.FIGHTERS_ONLY,
    description: 'Strength-enhancing gloves',
    stackable: false,
  },

  // =========================================================================
  // ACCESSORIES (ID 36-39)
  // =========================================================================

  36: {
    id: 36,
    name: 'Zeus Gauntlet',
    type: ItemType.ACCESSORY,
    power: 3,
    accuracy: 5,
    critBonus: 0,
    element: Element.LIGHTNING,
    spellEffect: 0,
    price: 15000,
    classRestriction: CLASS_RESTRICTIONS.ALL_CLASSES,
    description: 'Lightning-charged glove',
    stackable: false,
  },

  37: {
    id: 37,
    name: 'Protect Ring',
    type: ItemType.ACCESSORY,
    power: 5,
    accuracy: 5,
    critBonus: 0,
    element: Element.NONE,
    spellEffect: 0,
    price: 0,
    classRestriction: CLASS_RESTRICTIONS.ALL_CLASSES,
    description: 'Powerful defense ring',
    stackable: false,
  },

  38: {
    id: 38,
    name: 'Ruby Ring',
    type: ItemType.ACCESSORY,
    power: 2,
    accuracy: 0,
    critBonus: 0,
    element: Element.FIRE,
    spellEffect: 0,
    price: 3000,
    classRestriction: CLASS_RESTRICTIONS.ALL_CLASSES,
    description: 'Fire resistance ring',
    stackable: false,
  },

  39: {
    id: 39,
    name: 'Sapphire Ring',
    type: ItemType.ACCESSORY,
    power: 2,
    accuracy: 0,
    critBonus: 0,
    element: Element.ICE,
    spellEffect: 0,
    price: 3000,
    classRestriction: CLASS_RESTRICTIONS.ALL_CLASSES,
    description: 'Ice resistance ring',
    stackable: false,
  },

  // =========================================================================
  // CONSUMABLES (ID 40-50)
  // =========================================================================

  40: {
    id: 40,
    name: 'Potion',
    type: ItemType.CONSUMABLE,
    power: 50,
    accuracy: 100,
    critBonus: 0,
    element: Element.NONE,
    spellEffect: 0,
    price: 50,
    classRestriction: CLASS_RESTRICTIONS.CONSUMABLE,
    description: 'Restores 50 HP',
    stackable: true,
  },

  41: {
    id: 41,
    name: 'Hi-Potion',
    type: ItemType.CONSUMABLE,
    power: 150,
    accuracy: 100,
    critBonus: 0,
    element: Element.NONE,
    spellEffect: 0,
    price: 200,
    classRestriction: CLASS_RESTRICTIONS.CONSUMABLE,
    description: 'Restores 150 HP',
    stackable: true,
  },

  42: {
    id: 42,
    name: 'Ether',
    type: ItemType.CONSUMABLE,
    power: 1,
    accuracy: 100,
    critBonus: 0,
    element: Element.NONE,
    spellEffect: 0,
    price: 300,
    classRestriction: CLASS_RESTRICTIONS.CONSUMABLE,
    description: 'Restores 1 spell charge per level',
    stackable: true,
  },

  43: {
    id: 43,
    name: 'Antidote',
    type: ItemType.CONSUMABLE,
    power: 0,
    accuracy: 100,
    critBonus: 0,
    element: Element.NONE,
    spellEffect: StatusEffect.POISON,
    price: 75,
    classRestriction: CLASS_RESTRICTIONS.CONSUMABLE,
    description: 'Cures poison',
    stackable: true,
  },

  44: {
    id: 44,
    name: 'Gold Needle',
    type: ItemType.CONSUMABLE,
    power: 0,
    accuracy: 100,
    critBonus: 0,
    element: Element.NONE,
    spellEffect: StatusEffect.STONE,
    price: 400,
    classRestriction: CLASS_RESTRICTIONS.CONSUMABLE,
    description: 'Cures stone/petrify',
    stackable: true,
  },

  45: {
    id: 45,
    name: 'Phoenix Down',
    type: ItemType.CONSUMABLE,
    power: 25,
    accuracy: 100,
    critBonus: 0,
    element: Element.NONE,
    spellEffect: StatusEffect.DEAD,
    price: 500,
    classRestriction: CLASS_RESTRICTIONS.CONSUMABLE,
    description: 'Revives with 25% HP',
    stackable: true,
  },

  46: {
    id: 46,
    name: 'Tent',
    type: ItemType.CONSUMABLE,
    power: 100,
    accuracy: 100,
    critBonus: 0,
    element: Element.NONE,
    spellEffect: 0,
    price: 80,
    classRestriction: CLASS_RESTRICTIONS.CONSUMABLE,
    description: 'Full party heal (outside battle)',
    stackable: true,
  },

  47: {
    id: 47,
    name: 'Sleeping Bag',
    type: ItemType.CONSUMABLE,
    power: 50,
    accuracy: 100,
    critBonus: 0,
    element: Element.NONE,
    spellEffect: 0,
    price: 40,
    classRestriction: CLASS_RESTRICTIONS.CONSUMABLE,
    description: 'Partial party heal (outside battle)',
    stackable: true,
  },

  48: {
    id: 48,
    name: 'Eye Drops',
    type: ItemType.CONSUMABLE,
    power: 0,
    accuracy: 100,
    critBonus: 0,
    element: Element.NONE,
    spellEffect: StatusEffect.BLINDED,
    price: 50,
    classRestriction: CLASS_RESTRICTIONS.CONSUMABLE,
    description: 'Cures blindness',
    stackable: true,
  },

  49: {
    id: 49,
    name: 'Echo Herbs',
    type: ItemType.CONSUMABLE,
    power: 0,
    accuracy: 100,
    critBonus: 0,
    element: Element.NONE,
    spellEffect: StatusEffect.SILENCED,
    price: 100,
    classRestriction: CLASS_RESTRICTIONS.CONSUMABLE,
    description: 'Cures silence',
    stackable: true,
  },

  50: {
    id: 50,
    name: 'Elixir',
    type: ItemType.CONSUMABLE,
    power: 255,
    accuracy: 100,
    critBonus: 0,
    element: Element.NONE,
    spellEffect: 0,
    price: 5000,
    classRestriction: CLASS_RESTRICTIONS.CONSUMABLE,
    description: 'Full HP/MP/status restore',
    stackable: true,
  },

  // =========================================================================
  // ADDITIONAL WEAPONS/ARMOR (ID 51-60)
  // =========================================================================

  51: {
    id: 51,
    name: 'Cat Claw',
    type: ItemType.WEAPON,
    power: 16,
    accuracy: 25,
    critBonus: 15,
    element: Element.NONE,
    spellEffect: 0,
    price: 1200,
    classRestriction: CLASS_RESTRICTIONS.THIEF_ONLY,
    description: 'Thief special weapon',
    stackable: false,
  },

  52: {
    id: 52,
    name: 'Light Axe',
    type: ItemType.WEAPON,
    power: 40,
    accuracy: 20,
    critBonus: 15,
    element: Element.LIGHTNING,
    spellEffect: 0,
    price: 12000,
    classRestriction: CLASS_RESTRICTIONS.FIGHTERS_ONLY,
    description: 'Lightning-charged axe',
    stackable: false,
  },

  53: {
    id: 53,
    name: 'Mage Staff',
    type: ItemType.WEAPON,
    power: 12,
    accuracy: 15,
    critBonus: 0,
    element: Element.NONE,
    spellEffect: 0,
    price: 800,
    classRestriction: CLASS_RESTRICTIONS.MAGES_ONLY,
    description: 'Enhanced mage staff',
    stackable: false,
  },

  54: {
    id: 54,
    name: 'Healing Staff',
    type: ItemType.WEAPON,
    power: 8,
    accuracy: 10,
    critBonus: 0,
    element: Element.HOLY,
    spellEffect: 0,
    price: 3000,
    classRestriction: CLASS_RESTRICTIONS.WHITE_MAGE_ONLY,
    description: 'White mage staff',
    stackable: false,
  },

  55: {
    id: 55,
    name: 'Power Staff',
    type: ItemType.WEAPON,
    power: 15,
    accuracy: 20,
    critBonus: 5,
    element: Element.NONE,
    spellEffect: 0,
    price: 5000,
    classRestriction: CLASS_RESTRICTIONS.MAGES_ONLY,
    description: 'Powerful magic staff',
    stackable: false,
  },

  56: {
    id: 56,
    name: 'Sage Staff',
    type: ItemType.WEAPON,
    power: 20,
    accuracy: 25,
    critBonus: 0,
    element: Element.NONE,
    spellEffect: 0,
    price: 15000,
    classRestriction: CLASS_RESTRICTIONS.MAGES_ONLY,
    description: 'Legendary mage staff',
    stackable: false,
  },

  57: {
    id: 57,
    name: 'Vorpal Sword',
    type: ItemType.WEAPON,
    power: 45,
    accuracy: 35,
    critBonus: 30,
    element: Element.NONE,
    spellEffect: 0,
    price: 0,
    classRestriction: CLASS_RESTRICTIONS.FIGHTERS_ONLY,
    description: 'Rare instant-death blade',
    stackable: false,
  },

  58: {
    id: 58,
    name: 'Excalibur',
    type: ItemType.WEAPON,
    power: 60,
    accuracy: 50,
    critBonus: 20,
    element: Element.HOLY,
    spellEffect: 0,
    price: 0,
    classRestriction: CLASS_RESTRICTIONS.FIGHTERS_ONLY,
    description: 'Ultimate holy blade',
    stackable: false,
  },

  59: {
    id: 59,
    name: 'Thor Hammer',
    type: ItemType.WEAPON,
    power: 28,
    accuracy: 25,
    critBonus: 10,
    element: Element.LIGHTNING,
    spellEffect: 0,
    price: 6000,
    classRestriction: CLASS_RESTRICTIONS.FIGHTERS_ONLY,
    description: 'Hammer of thunder',
    stackable: false,
  },

  60: {
    id: 60,
    name: 'Sasuke Katana',
    type: ItemType.WEAPON,
    power: 50,
    accuracy: 40,
    critBonus: 30,
    element: Element.NONE,
    spellEffect: 0,
    price: 0,
    classRestriction: CLASS_RESTRICTIONS.THIEF_ONLY,
    description: 'Legendary thief blade',
    stackable: false,
  },
};

/**
 * Get item by ID
 */
export function getItem(itemId: number): ItemDefinition | null {
  return ITEMS[itemId] || null;
}

/**
 * Check if a class can use an item
 */
export function canClassUseItem(itemId: number, characterClass: CharacterClass): boolean {
  const item = getItem(itemId);
  if (!item) return false;

  const classBit = 1 << characterClass;
  return (item.classRestriction & classBit) !== 0;
}

/**
 * Get all items of a specific type
 */
export function getItemsByType(type: ItemType): ItemDefinition[] {
  return Object.values(ITEMS).filter(item => item.type === type);
}

/**
 * Get consumable items
 */
export function getConsumables(): ItemDefinition[] {
  return getItemsByType(ItemType.CONSUMABLE);
}

/**
 * Get equipment items
 */
export function getEquipment(): ItemDefinition[] {
  return Object.values(ITEMS).filter(item =>
    item.type !== ItemType.CONSUMABLE && item.type !== ItemType.KEY_ITEM
  );
}
